import { Box, Flex, Text } from '@chakra-ui/react';
import { Notes } from '@codametrix/shared-views';
import React from 'react';

import type { SlotProps } from '../../../project_starburst';
import { asNotesData } from './asNotesData';
import { noteKey } from './noteKey';
import { useNoteInteraction } from './noteInteractionContext';
import { SourceDocumentModal } from './SourceDocumentModal';
import { CmxDocumentContentStream } from './CmxDocumentContentStream';

/**
 * The LEFT-pane note body (`left.viewer`'s `'NoteViewer'` slot) -- resolves the
 * "active" note (`activeNoteKey`, shared with `NoteNav` via `useNoteInteraction()`, defaulting to
 * the first note when nothing is explicitly selected -- AC8's "navigate between notes" is just
 * `setActiveNoteKey`, read back here), and renders the note body through the SAME shared-views
 * `<Notes>` component the existing screens use -- passed a single-note array so its own
 * formatting/highlighting dispatch (by `filetype`) applies unchanged, read-only (`disabled`, AC9/
 * AC10). The note's TYPE label and the source-document trigger live on the `NoteNav` strip above
 * (the note tabs, AC4-AC7), NOT on this body -- there is no per-viewer header, since it would just
 * duplicate the tabs. This body still MOUNTS the shared `SourceDocumentModal` (opened from a
 * `NoteNav` icon via `useNoteInteraction()`), so the modal renders within the viewer region.
 * Empty-state (AC13) when there are no notes at all.
 */
export const NoteViewer: React.FC<SlotProps> = ({ value }) => {
  const { notes, cmxDocumentContents } = asNotesData(value);
  const { activeNoteKey } = useNoteInteraction();

  const activeCmxDocumentContent = cmxDocumentContents.find(
    (entry) => entry.documentId === activeNoteKey
  );

  if (activeCmxDocumentContent) {
    return (
      <>
        <CmxDocumentContentStream cmxDocumentContents={cmxDocumentContents} />
        <SourceDocumentModal
          notes={notes}
          cmxDocumentContents={cmxDocumentContents}
        />
      </>
    );
  }

  if (notes.length === 0) {
    return (
      <Flex
        role="region"
        aria-label="Note viewer"
        data-testid="note-viewer-empty"
        align="center"
        justify="center"
        p={4}
      >
        <Text color="gray.500">No notes available</Text>
      </Flex>
    );
  }

  const activeNote =
    notes.find((note) => noteKey(note) === activeNoteKey) ?? notes[0];

  return (
    <Box role="region" aria-label="Note viewer" data-testid="note-viewer">
      <Box
        data-testid="note-viewer-body"
        overflowX="auto"
        sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        <Notes notes={[activeNote]} disabled />
      </Box>
      <SourceDocumentModal
        notes={notes}
        cmxDocumentContents={cmxDocumentContents}
      />
    </Box>
  );
};
